import { create } from 'zustand';

/**
 * demo-mode-store — agency-wide "clickthrough mode" for demos,
 * training, and walkthroughs with stakeholders.
 *
 * When demo mode is ON:
 *   - Finalization validators emit warnings instead of blockers so a
 *     presenter can click Finalize / Approve / Lock without entering
 *     real demographics or vitals
 *   - Forms offer one-tap "fill demo data" buttons that populate
 *     plausible placeholder content
 *   - A persistent red "DEMO" banner sits across the top of the
 *     screen so nobody confuses the session for production use
 *   - User-visible data entered in demo mode is tagged with a
 *     .demo_mode flag on the ePCR / incident so downstream reports
 *     can filter it out of real metrics
 *
 * Persisted to localStorage under 'emspcr-demo-mode' so the flag
 * survives refreshes through a demo session.
 */

const STORAGE_KEY = 'emspcr-demo-mode';

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

function writeStored(on: boolean): void {
  try {
    if (on) localStorage.setItem(STORAGE_KEY, 'on');
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

interface DemoState {
  enabled: boolean;
  toggle: () => void;
  setEnabled: (on: boolean) => void;
  init: () => void;
}

export const useDemoMode = create<DemoState>((set, get) => ({
  enabled: false,
  toggle: () => {
    const next = !get().enabled;
    set({ enabled: next });
    writeStored(next);
  },
  setEnabled: (on) => {
    set({ enabled: on });
    writeStored(on);
  },
  init: () => {
    set({ enabled: readStored() });
  },
}));

/** Read-only hook for components that only want to check the flag. */
export function useIsDemoMode(): boolean {
  return useDemoMode((s) => s.enabled);
}

/** Synchronous read for non-React callers (validators, services). */
export function isDemoModeActive(): boolean {
  return useDemoMode.getState().enabled;
}
